/**
 * Parse shop and host from URL query params for embedded Shopify app context.
 * Used by main.tsx, AuthGate, and OAuth flows.
 *
 * shop/host are present only on the initial embed URL. On internal navigation
 * they disappear from the URL, so we persist them in sessionStorage as fallback.
 */

export interface ShopContext {
  shop: string | null;
  host: string | null;
}

const SESSION_KEY_SHOP = 'shopify_shop';
const SESSION_KEY_HOST = 'shopify_host';

function saveToSession(shop: string | null, host: string | null) {
  try {
    if (shop) sessionStorage.setItem(SESSION_KEY_SHOP, shop);
    if (host) sessionStorage.setItem(SESSION_KEY_HOST, host);
  } catch {
    // sessionStorage unavailable (private mode / cross-origin iframe restrictions)
  }
}

function loadFromSession(): ShopContext {
  try {
    return {
      shop: sessionStorage.getItem(SESSION_KEY_SHOP),
      host: sessionStorage.getItem(SESSION_KEY_HOST),
    };
  } catch {
    return { shop: null, host: null };
  }
}

/**
 * Parse shop and host from current URL, with sessionStorage fallback.
 * - Prefer ?shop if it looks like *.myshopify.com
 * - Else if ?host exists: try base64 decode; parse admin.shopify.com/store/<store> -> <store>.myshopify.com
 * - If URL params are absent (internal navigation), fall back to sessionStorage values saved on first load.
 */
export function parseShopContext(): ShopContext {
  if (typeof window === 'undefined') {
    return { shop: null, host: null };
  }

  const urlParams = new URLSearchParams(window.location.search);
  const shopParam = urlParams.get('shop');
  const hostParam = urlParams.get('host');

  let shop: string | null = null;
  let host: string | null = hostParam;

  if (shopParam) {
    if (shopParam.includes('.myshopify.com')) {
      shop = shopParam;
    }
  }

  if (!shop && hostParam) {
    try {
      const decoded = atob(hostParam);
      const match = decoded.match(/store\/([^/]+)/);
      if (match) {
        shop = `${match[1]}.myshopify.com`;
      }
    } catch {
      // Not base64
    }
  }

  if (shopParam && !shopParam.includes('.myshopify.com') && shopParam.includes('myshopify.com')) {
    shop = shopParam;
  }

  // If we found values from the URL, persist them for future navigation
  if (shop || host) {
    saveToSession(shop, host);
    return { shop, host };
  }

  // Fallback: URL params are gone (internal navigation) – read from sessionStorage
  return loadFromSession();
}
